'use client';

import * as React from 'react';

import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

import { UnstyledButton } from '..';
import {
  CheckmarkCircleFilled,
  Dismiss,
  DismissCircleFilled,
  ErrorCircleFilled,
  QuestionCircleFilled,
} from '../../icons';

const bannerVariants = tv({
  slots: {
    root: 'flex items-center justify-between p-2 border rounded',
    icon: 'shrink-0',
  },
  variants: {
    variant: {
      success: {
        root: 'border-green-500 bg-green-50',
        icon: 'text-green-500',
      },
      alert: {
        root: 'border-yellow-500 bg-yellow-50',
        icon: 'text-yellow-500',
      },
      error: {
        root: 'border-red-500 bg-red-50',
        icon: 'text-red-500',
      },
      info: {
        root: 'border-primary-100 bg-primary-50',
        icon: 'text-primary-500 ',
      },
    },
  },
  defaultVariants: {
    variant: 'info',
  },
});

export type BannerProps = {
  isOpen?: boolean;
  text?: string;
  showCloseButton?: boolean;
  onClose?: () => void;
} & React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof bannerVariants>;

const icons = {
  success: CheckmarkCircleFilled,
  alert: ErrorCircleFilled,
  error: DismissCircleFilled,
  info: QuestionCircleFilled,
};

const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  ({ className, variant = 'info', showCloseButton = true, ...props }, ref) => {
    const { root, icon } = bannerVariants({
      variant,
      className,
    });

    const Icon = icons[variant];

    return (
      <div
        className={root({
          className,
        })}
        ref={ref}
        {...props}
      >
        <div className="flex gap-3">
          <Icon className={icon()} height={24} width={24} />
          <span className="text-body-3 text-gray-900">
            {props.children ?? props.text}
          </span>
          {showCloseButton ? (
            <UnstyledButton
              onPress={() => {
                props.onClose?.();
              }}
            >
              <Dismiss className="text-gray-400" height={20} width={20} />
            </UnstyledButton>
          ) : null}
        </div>
      </div>
    );
  },
);

Banner.displayName = 'Banner';

export { Banner, bannerVariants };
